import Skeleton from "@ui-kit/feedback/Skeleton/Skeleton.web";
import TickCircle from "@ui-kit/icons/TickCircle/TickCircle.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { useTranslation } from "@shared/utils/internationalization";
import { RefundProtectionClaim } from "@app-contexts/user/features/my-orders/components/RefundProtection/RefundProtectionClaim.web";
import type { OrderStatusSharedProps } from "./OrderStatus.props";
import ReturnToExploreButton from "../OrderStatusButtons/ReturnToExploreButton/ReturnToExploreButton.web";
import AddToDigitalWalletButton from "../OrderStatusButtons/AddToDigitalWalletButton/AddToDigitalWalletButton.web";
import ViewOrderButton from "../OrderStatusButtons/ViewOrderButton/ViewOrderButton.web";
import DownloadTicketsButton from "../OrderStatusButtons/DownloadTicketsButton/DownloadTicketsButton.web";

export default function OrderStatus(props: OrderStatusProps): JSX.Element {
  const {
    transactionId,
    isConfirmationEmailEnabled,
    hasNfcEvent,
    isLoggedIn,
    isVirtualEvent,
    isLoading,
    basket,
    protection,
  } = props;

  const { t } = useTranslation("checkout");
  const myOrderUrl = `/account/my-orders/${transactionId}`;
  const stagingPageUrl = `/account/my-orders/invoice-staging/?uuid1=${basket?.uuid}&uuid2=${basket?.uuid2}&transactionId=${transactionId}`;

  if (isLoading)
    return (
      <Flex direction="column" align="center" gap="xl">
        <Skeleton>
          <Box h="element.4xl" w="element.4xl" />
        </Skeleton>
        <Skeleton h="element.8xl" w="100%" />
        <Flex gap="md" direction={{ base: "column", sm: "row" }} width="full">
          <Skeleton h="element.3xl" w="100%" />
          <Skeleton h="element.3xl" w="100%" />
        </Flex>
      </Flex>
    );

  const isWaitlist = basket?.is_waitlist;

  return (
    <Flex
      direction="column"
      align="center"
      gap="xl"
      data-testid="order-status"
      textAlign="center"
    >
      <TickCircle.Solid
        w="element.4xl"
        h="element.4xl"
        colorPalette="primary"
      />
      <Flex direction="column" align="center">
        <Text fontSize="4xl" fontWeight={600} color="primary.800" mb="xs">
          {!isWaitlist ? t("Order confirmed") : t("You're on the list!")}
        </Text>
        {transactionId && (
          <Text>
            <Text as="span">{t("Order ID")}</Text>{" "}
            <Text
              as="span"
              fontSize="sm"
              fontWeight={700}
              data-testid="order-status-transaction-id"
            >
              {transactionId}
            </Text>
          </Text>
        )}

        {isWaitlist && (
          <Text mt="md">
            <Text as="span">
              {t("Your waitlist confirmation has been sent to")}{" "}
            </Text>
            <Text
              as="span"
              fontWeight={700}
              data-testid="order-status-waitlist-email"
            >
              {basket?.email}{" "}
            </Text>
          </Text>
        )}

        {/* show if it enabled on venue */}
        {!isWaitlist && isConfirmationEmailEnabled && basket?.email && (
          <Text mt="md">
            <Text as="span">
              {t("Your confirmation and tickets have been sent to")}{" "}
            </Text>
            <Text as="span" fontWeight={700} data-testid="order-status-email">
              {basket?.email}{" "}
            </Text>
          </Text>
        )}
      </Flex>

      {transactionId && (
        <Flex gap="md" direction={{ base: "column", sm: "row" }} width="full">
          {isLoggedIn && !isVirtualEvent ? (
            <DownloadTicketsButton
              transactionId={transactionId}
              myOrderUrl={myOrderUrl}
            />
          ) : null}

          {/* user can add to digital wallet if order doesn't include NFC events */}
          {!hasNfcEvent ? (
            <AddToDigitalWalletButton
              isLoggedIn={isLoggedIn}
              myOrderUrl={myOrderUrl}
              stagingPageUrl={stagingPageUrl}
              transactionId={transactionId}
            />
          ) : null}

          <ReturnToExploreButton />

          <ViewOrderButton
            isLoggedIn={isLoggedIn}
            myOrderUrl={myOrderUrl}
            stagingPageUrl={stagingPageUrl}
            transactionId={transactionId}
          />
        </Flex>
      )}
      {protection ? (
        <RefundProtectionClaim
          provider="Teak"
          transactionId={transactionId}
          protection={protection}
          basketId={basket?.id ?? 0}
          uuid1={basket?.uuid ?? ""}
          uuid2={basket?.uuid2 ?? ""}
          isBasketClaimed={Boolean(basket?.user_id)}
          withActionButtons={false}
        />
      ) : null}
    </Flex>
  );
}

export type OrderStatusProps = OrderStatusSharedProps & {
  // Add any web specific props here.
};
